/**
 * Agent Scheduler Page
 * AIDLC Administrative Backend
 *
 * Sam's scheduling control panel. Configure wake-up intervals, toggle
 * event triggers, manage budget-aware dormancy settings, and view
 * wake-up history. Consumes the backend /api/scheduler endpoints.
 *
 * Personas: Ops, Orchestrator
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtDateTime } from '../../utils/formatters';
import type {
  SchedulerConfig,
  SchedulerTrigger,
  DormancySettings,
  WakeUpHistoryEntry,
} from '../../services/orchestrationService';
import {
  getSchedulerConfig,
  updateSchedulerConfig,
  triggerWake,
  getWakeUpHistory,
} from '../../services/orchestrationService';

/** Compute relative time string from now */
function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'now';
  const hours = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return `in ${hours}h ${mins}m`;
  return `in ${mins}m`;
}

/** Interval options */
const INTERVAL_OPTIONS = [6, 12, 24, 48] as const;

/** Trigger metadata (camelCase keys matching backend) */
const TRIGGER_META: Record<SchedulerTrigger, { label: string; description: string }> = {
  ceoDirective: {
    label: 'CEO Directive',
    description: 'Wake when the CEO posts a new directive to AgentHub',
  },
  newBacklogItems: {
    label: 'New Backlog Items',
    description: 'Wake when new items are added to the product backlog',
  },
  agentCompletion: {
    label: 'Agent Completion',
    description: 'Wake when a spawned agent completes its assigned task',
  },
  blockerDetected: {
    label: 'Blocker Detected',
    description: 'Wake when a blocker is detected on a sprint item',
  },
  budgetReplenishment: {
    label: 'Budget Replenishment',
    description: 'Wake when token budget is replenished or increased',
  },
  milestoneGateReached: {
    label: 'Milestone Gate Reached',
    description: 'Wake when a milestone gate passes or fails',
  },
};

/** Trigger badge variant */
function triggerBadgeVariant(trigger: string): 'info' | 'warning' | 'success' | 'danger' | 'purple' | 'neutral' {
  switch (trigger) {
    case 'ceoDirective': return 'danger';
    case 'newBacklogItems': return 'info';
    case 'agentCompletion': return 'success';
    case 'blockerDetected': return 'warning';
    case 'budgetReplenishment': return 'purple';
    case 'milestoneGateReached': return 'info';
    case 'manual': return 'neutral';
    default: return 'neutral';
  }
}

/** Readable trigger label */
function triggerLabel(trigger: string): string {
  if (trigger === 'manual') return 'Manual';
  return TRIGGER_META[trigger as SchedulerTrigger]?.label || trigger;
}

// ── Main Component ───────────────────────────────────────────────────

export function AgentSchedulerPage() {
  const [config, setConfig] = useState<SchedulerConfig | null>(null);
  const [history, setHistory] = useState<WakeUpHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isWaking, setIsWaking] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [wakeSuccess, setWakeSuccess] = useState(false);

  // Editable local state derived from config
  const [intervalHours, setIntervalHours] = useState<number>(24);
  const [triggers, setTriggers] = useState<Record<SchedulerTrigger, boolean>>({
    ceoDirective: true,
    newBacklogItems: true,
    agentCompletion: false,
    blockerDetected: true,
    budgetReplenishment: false,
    milestoneGateReached: false,
  });
  const [dormancy, setDormancy] = useState<DormancySettings>({
    budgetThresholdPercent: 20,
    reducedIntervalHours: 48,
    fullDormancyAtZero: true,
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [configData, historyData] = await Promise.all([
        getSchedulerConfig(),
        getWakeUpHistory(),
      ]);
      setConfig(configData);
      setHistory(historyData);
      // Sync local editable state
      setIntervalHours(configData.intervalHours);
      setTriggers(configData.triggers);
      setDormancy(configData.dormancy);
    } catch (err) {
      console.error('[AgentScheduler] Failed to load:', err);
      setError('Failed to load scheduler configuration. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    try {
      const updated = await updateSchedulerConfig({
        intervalHours,
        triggers,
        dormancy,
      });
      setConfig(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('[AgentScheduler] Failed to save:', err);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleWake = async () => {
    setIsWaking(true);
    setWakeSuccess(false);
    try {
      await triggerWake();
      setWakeSuccess(true);
      setTimeout(() => setWakeSuccess(false), 3000);
      // Refresh data to get updated lastWakeUp and history
      await loadData();
    } catch (err) {
      console.error('[AgentScheduler] Failed to wake:', err);
      setError('Failed to trigger wake. Please try again.');
    } finally {
      setIsWaking(false);
    }
  };

  const toggleTrigger = (key: SchedulerTrigger) => {
    setTriggers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading scheduler configuration..." />;
  }

  if (error && !config) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Scheduler</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Agent Scheduler</h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure Sam's wake-up schedule, event triggers, and budget-aware dormancy settings.
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Inline error banner */}
      {error && config && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Schedule Configuration */}
      <Card title="Schedule Configuration" subtitle="Set the wake-up interval and trigger a manual wake" className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Interval + Wake button */}
          <div className="space-y-4">
            {/* Interval selector */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Wake-Up Interval
              </label>
              <div className="flex items-center gap-1">
                {INTERVAL_OPTIONS.map((hrs) => (
                  <button
                    key={hrs}
                    onClick={() => setIntervalHours(hrs)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      intervalHours === hrs
                        ? 'bg-ldaja-blue text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {hrs}h
                  </button>
                ))}
              </div>
            </div>

            {/* Wake Sam Now */}
            <div>
              <Button
                variant="primary"
                size="lg"
                isLoading={isWaking}
                onClick={handleWake}
                className="w-full sm:w-auto"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Wake Sam Now
              </Button>
              {wakeSuccess && (
                <p className="text-xs text-green-600 mt-2">Sam has been woken up successfully.</p>
              )}
            </div>
          </div>

          {/* Right: Timing info */}
          <div className="space-y-3">
            {/* Next Wake-Up */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Next Wake-Up</p>
              {config?.nextWakeUp ? (
                <div>
                  <p className="text-lg font-bold text-ldaja-blue">{fmtDateTime(config.nextWakeUp)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{timeUntil(config.nextWakeUp)}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400">Not scheduled</p>
              )}
            </div>

            {/* Last Wake-Up */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Last Wake-Up</p>
              {config?.lastWakeUp ? (
                <p className="text-sm font-medium text-gray-700">{fmtDateTime(config.lastWakeUp)}</p>
              ) : (
                <p className="text-sm text-gray-400">Never</p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Trigger Toggles */}
      <Card title="Event Triggers" subtitle="Toggle which events can wake Sam outside the regular interval" className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(Object.keys(TRIGGER_META) as SchedulerTrigger[]).map((key) => {
            const meta = TRIGGER_META[key];
            const enabled = triggers[key];
            return (
              <div
                key={key}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  enabled
                    ? 'border-ldaja-blue/30 bg-blue-50/30'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {/* Toggle button */}
                <button
                  onClick={() => toggleTrigger(key)}
                  className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:ring-offset-2 ${
                    enabled ? 'bg-ldaja-blue' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={enabled}
                  aria-label={`Toggle ${meta.label}`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-4' : 'translate-x-1'
                    }`}
                  />
                </button>

                {/* Label + description */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{meta.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{meta.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Save button */}
        <div className="mt-4 flex items-center gap-3">
          <Button variant="primary" size="md" isLoading={isSaving} onClick={handleSave}>
            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M5 13l4 4L19 7" />
            </svg>
            Save Configuration
          </Button>
          {saveSuccess && (
            <span className="text-xs text-green-600">Configuration saved successfully.</span>
          )}
        </div>
      </Card>

      {/* Budget-Aware Dormancy */}
      <Card title="Budget-Aware Dormancy" subtitle="Control scheduling behaviour when token budget runs low" className="mb-6">
        <DormancySection
          dormancy={dormancy}
          onChange={setDormancy}
        />
      </Card>

      {/* Wake-Up History */}
      <Card title="Wake-Up History" subtitle="Recent wake-up events and their outcomes" className="mb-6">
        <WakeUpHistoryTable history={history} />
      </Card>
    </div>
  );
}

// ── Dormancy Section ─────────────────────────────────────────────────

function DormancySection({
  dormancy,
  onChange,
}: {
  dormancy: DormancySettings;
  onChange: (d: DormancySettings) => void;
}) {
  const thresholdPct = dormancy.budgetThresholdPercent;

  // Dormancy status indicator
  let dormancyLabel = 'Normal Operation';
  let dormancyVariant: 'success' | 'warning' | 'danger' = 'success';
  if (thresholdPct >= 50) {
    dormancyLabel = 'Aggressive Dormancy';
    dormancyVariant = 'danger';
  } else if (thresholdPct >= 20) {
    dormancyLabel = 'Standard Threshold';
    dormancyVariant = 'warning';
  }

  return (
    <div className="space-y-5">
      {/* Status indicator */}
      <div className="flex items-center gap-3">
        <Badge variant={dormancyVariant} dot>{dormancyLabel}</Badge>
        <span className="text-xs text-gray-500">
          Dormancy activates below {thresholdPct}% budget remaining
        </span>
      </div>

      {/* Budget threshold slider */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Budget Threshold: <span className="text-ldaja-blue font-bold">{thresholdPct}%</span>
        </label>
        <input
          type="range"
          min={5}
          max={75}
          step={5}
          value={thresholdPct}
          onChange={(e) => onChange({ ...dormancy, budgetThresholdPercent: Number(e.target.value) })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-ldaja-blue"
        />
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>5%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
        </div>
      </div>

      {/* Reduced interval */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">
          Reduced Interval When Low Budget
        </label>
        <div className="flex items-center gap-1">
          {[24, 48, 72, 96].map((hrs) => (
            <button
              key={hrs}
              onClick={() => onChange({ ...dormancy, reducedIntervalHours: hrs })}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                dormancy.reducedIntervalHours === hrs
                  ? 'bg-ldaja-blue text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {hrs}h
            </button>
          ))}
        </div>
      </div>

      {/* Full dormancy toggle */}
      <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200">
        <button
          onClick={() => onChange({ ...dormancy, fullDormancyAtZero: !dormancy.fullDormancyAtZero })}
          className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50 focus:ring-offset-2 ${
            dormancy.fullDormancyAtZero ? 'bg-ldaja-blue' : 'bg-gray-300'
          }`}
          role="switch"
          aria-checked={dormancy.fullDormancyAtZero}
          aria-label="Toggle full dormancy at zero budget"
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
              dormancy.fullDormancyAtZero ? 'translate-x-4' : 'translate-x-1'
            }`}
          />
        </button>
        <div>
          <p className="text-sm font-medium text-gray-800">Full Dormancy at Zero Budget</p>
          <p className="text-xs text-gray-500 mt-0.5">
            When enabled, Sam will not wake at all when the token budget reaches zero.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Wake-Up History Table ────────────────────────────────────────────

function WakeUpHistoryTable({ history }: { history: WakeUpHistoryEntry[] }) {
  if (history.length === 0) {
    return (
      <EmptyState
        title="No wake-up history"
        description="Sam hasn't been woken up yet. Use the Wake Sam Now button to trigger the first wake-up."
      />
    );
  }

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
            <th className="px-4 py-2 font-medium">Timestamp</th>
            <th className="px-4 py-2 font-medium">Trigger</th>
            <th className="px-4 py-2 font-medium">Result</th>
            <th className="px-4 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {history.map((entry) => (
            <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                {fmtDateTime(entry.timestamp)}
              </td>
              <td className="px-4 py-3">
                <Badge variant={triggerBadgeVariant(entry.triggeredBy)} dot>
                  {triggerLabel(entry.triggeredBy)}
                </Badge>
              </td>
              <td className="px-4 py-3 text-gray-700">{entry.assessmentId || '—'}</td>
              <td className="px-4 py-3 text-gray-500 text-xs">{entry.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
